import {
  CloseOutlined,
  EllipsisOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { Form, Table, Dropdown, Space, Badge } from "antd";
import { PrimaryButton, SecondaryButton } from "components/common/Buttons";
import React, { useCallback, useContext, useState } from "react";
import moment from "moment";
import { useNavigate, Link } from "react-router-dom";
import { capitalize } from "utils/Capitalize";
import { useCuarentenas } from "./hooks/useCuarentenas";

import CustomSearch from "components/common/Forms/CustomSearch";
import FiltrosTablaCuarentenas from "components/Drawer/FiltrosTablaCuarentenas";
import { AppContext } from "app/AppContext";
import { userHasPermission } from "utils/userHasPermission";
import { CUARENTENAS } from "app_permissions/cuarentenas";
import {
  AlimentoIcon,
  CerdoIcon,
  PavoIcon,
  PolloIcon,
} from "components/common/Icons/Icons";
import { CODE_ESPECIES } from "constants/enums";
import { debounce } from "lodash";

const TableCuarentenasByStatus = () => {
  const [form] = Form.useForm();
  const [showDrawer, setShowDrawer] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [shouldReset, setShouldReset] = useState(false);

  const {
    tableData,
    total,
    isDataFetching,
    isFiltering,
    fetchConfig,
    setFetchConfig,

    setFilterValue,

    setIsFiltering,
    setIsDataFetching,
    setTableData,
    setTotal,
  } = useCuarentenas();

  const { rolData } = useContext(AppContext);
  const navigate = useNavigate();

  const CREAR_CUARENTENA = userHasPermission(rolData?.role, CUARENTENAS?.CREAR);

  const formatDate = (dateTimestamp) =>
    moment.utc(Number(dateTimestamp)).format("DD/MM/YYYY");

  const handleSearchDebounced = useCallback(
    debounce((value) => {
      setIsFiltering(false);
      setFilterValue((prev) => ({
        ...prev,
        keyword: value,
      }));
      setFetchConfig((prev) => ({
        ...prev,
        offset: 1,
      }));
    }, 500),
    []
  );

  const handleSearchInputChange = (value) => {
    setSearchTerm(value);
    handleSearchDebounced(value);
  };

  const handlePaginationChange = (pagination) => {
    setFetchConfig((prev) => ({
      ...prev,
      offset: pagination.current,
      limit: pagination.pageSize,
    }));
  };

  const handleResetFilters = () => {
    setFilterValue({
      start_date: undefined,
      end_date: undefined,
      status: [],
      type_quarantine: [],
      specie: [],
      keyword: "",
    });

    setFetchConfig({
      offset: 1,
      limit: 10,
      order: "DESC",
    });

    setIsFiltering(false);
  };

  const handleFilterValues = (filter) => {
    setFilterValue(filter);
    setIsFiltering(true);
  };

  const columns = [
    {
      title: "Id",
      dataIndex: "id",
      fixed: true,
    },
    {
      title: "Creador",
      render: (record) =>
        `${capitalize(record?.createdBy?.person?.name)} ${capitalize(
          record?.createdBy?.person?.lastname
        )}`,
    },
    {
      title: "Especie",
      render: (record) => {
        if (!record?.specie?.code) {
          return "-";
        }
        switch (record.specie.code) {
          case CODE_ESPECIES.CERDOS:
            return <CerdoIcon />;
          case CODE_ESPECIES.POLLOS:
            return <PolloIcon />;
          case CODE_ESPECIES.PAVOS:
            return <PavoIcon />;
          case CODE_ESPECIES.PLANTA_ALIMENTOS:
            return <AlimentoIcon />;
          default:
            return "-";
        }
      },
    },
    {
      title: "Tipo",
      render: (record) => capitalize(record?.typeQuarantine?.name || "-"),
    },
    {
      title: "Sectores",
      render: (record) =>
        capitalize(record?.sectorsNames || record?.foodPlantsNames),
    },
    {
      title: "Fecha inicio",
      render: (record) =>
        record?.start_date ? formatDate(record?.start_date) : "-",
    },
    {
      title: "Fecha término",
      render: (record) =>
        record?.end_date ? formatDate(record?.end_date) : "-",
    },
    {
      title: "Vigencia",
      render: (record) => (
        <Badge
          text={statusName[record?.status_id]}
          status={status[record?.status_id]}
        />
      ),
    },
    {
      title: "Acción",
      render: (record) => {
        const actions = [
          {
            key: "1",
            label: (
              <Link
                state={{
                  cuarentenaId: record.id,
                  sectorId: record.sector_id,
                }}
                to={`/cuarentenas/${record.id}/ver-detalle`}
              >
                Ver Detalle
              </Link>
            ),
          },
        ];
        if (record.status?.code !== "INVD") {
          actions.push(
            {
              key: "2",
              label: (
                <Link
                  state={{ cuarentenaId: record.id }}
                  to={`/cuarentenas/editar-cuarentena/${record.id}`}
                >
                  Editar
                </Link>
              ),
            },
            {
              key: "3",
              label: (
                <Link
                  state={{ cuarentenaId: record.id, invalidar: true }}
                  to={`/cuarentenas/${record.id}/ver-detalle`}
                >
                  Invalidar
                </Link>
              ),
            }
          );
        }
        return (
          <Dropdown menu={{ items: actions }} trigger={["click"]}>
            <Space>
              <EllipsisOutlined rotate={90} />
            </Space>
          </Dropdown>
        );
      },
    },
  ];

  const status = {
    1: "success",
    2: "error",
    3: "default",
    9: "default",
    11: "error",
  };

  const statusName = {
    1: "Vigente",
    2: "...",
    3: "Pendiente",
    9: "Finalizada",
    11: "Invalidada",
  };

  return (
    <div className="tabla-cuarentenas-container">
      <div className="filter_container">
        <div className="filter_input">
          <CustomSearch
            loading={isDataFetching}
            value={searchTerm}
            onChange={(e) => handleSearchInputChange(e.target.value)}
          />
          <SecondaryButton onButtonClick={() => setShowDrawer(true)} width={90}>
            Filtros
          </SecondaryButton>
          {isFiltering && (
            <PrimaryButton
              className="mobile-filters"
              onButtonClick={handleResetFilters}
            >
              <CloseOutlined />
            </PrimaryButton>
          )}
        </div>
        {CREAR_CUARENTENA && (
          <div className="cuarentenas-btn">
            <PrimaryButton
              onButtonClick={() => navigate("/cuarentenas/nueva-cuarentena")}
              width={180}
              icon={<PlusCircleOutlined />}
            >
              Crear
            </PrimaryButton>
          </div>
        )}
      </div>
      <Form form={form} component={false}>
        <Table
          rowKey="id"
          loading={isDataFetching}
          columns={columns}
          dataSource={tableData}
          scroll={{ x: "max-content" }}
          pagination={{
            showSizeChanger: true,
            pageSize: fetchConfig.limit,
            current: fetchConfig.offset,
            total: total,
            showTotal: (total, range) =>
              `${range[0]} a ${range[1]} de ${total} resultados`,
          }}
          onChange={handlePaginationChange}
        />
      </Form>
      <FiltrosTablaCuarentenas
        visible={showDrawer}
        onClose={() => setShowDrawer(false)}
        setTableData={setTableData}
        setIsFiltering={setIsFiltering}
        setIsDataFetching={setIsDataFetching}
        setTotal={setTotal}
        shouldReset={shouldReset}
        setShouldReset={setShouldReset}
        handleFilterValues={handleFilterValues}
      />
    </div>
  );
};

export default TableCuarentenasByStatus;
